import React, { useState, useEffect } from 'react';
import './Dashboard.css';
import { logout } from '../../firebase/authService';
import { getAllImages, getVisitorCount, getCountriesStats, getUnreadMessagesCount, getAllMessages } from '../../firebase/dataService';
import ImageManager from './ImageManager';
import AddImage from './AddImage';
import AdminImageDetail from './AdminImageDetail';
import CategoryManager from './CategoryManager';
import ContactManager from './ContactManager';
import AboutManager from './AboutManager';
import MessagesManager from './MessagesManager';
import UserManager from './UserManager';
import TopicsManagement from './TopicsManagement';

function Dashboard({ user, onLogout }) {
  const [activeTab, setActiveTab] = useState(null);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [totalMessages, setTotalMessages] = useState(0);
  const [stats, setStats] = useState({
    total: 0,
    visible: 0,
    hidden: 0
  });
  const [visitorCount, setVisitorCount] = useState(0);
  const [countriesStats, setCountriesStats] = useState([]);
  const [userRole, setUserRole] = useState('viewer');
  const [permissions, setPermissions] = useState({
    canManageUsers: false,
    canManageImages: false,
    canManageCategories: false,
    canViewStats: true,
    canManageContact: false,
    canManageAbout: false,
    canViewMessages: false,
    canManageTopics: false
  });
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    loadUserPermissions();
    loadImages();
    loadStats();
    loadUnreadMessages();
    loadTotalMessages();
  }, []);

  const loadUserPermissions = async () => {
    try {
      if (user) {
        // الحصول على صلاحيات المستخدم من Firestore
        const { getUserByEmail } = await import('../../firebase/userService');
        const userData = await getUserByEmail(user.email);
        
        if (userData && userData.role) {
          // فحص إذا كان الحساب معطلاً
          if (userData.active === false) {
            alert('حسابك معطل. يرجى التواصل مع المدير.');
            await logout();
            onLogout();
            return;
          }
          
          setUserRole(userData.role);
          const userPermissions = getPermissionsForRole(userData.role);
          setPermissions(userPermissions);
          
          // تحديد الصفحة الافتراضية بناءً على الصلاحيات
          if (!activeTab) {
            if (userPermissions.canViewStats) {
              setActiveTab('stats');
            } else if (userPermissions.canManageImages) {
              setActiveTab('images');
            } else if (userPermissions.canManageCategories) {
              setActiveTab('categories');
            } else if (userPermissions.canManageUsers) {
              setActiveTab('users');
            } else if (userPermissions.canManageContact) {
              setActiveTab('contact');
            } else if (userPermissions.canManageAbout) {
              setActiveTab('about');
            } else if (userPermissions.canViewMessages) {
              setActiveTab('messages');
            }
          }
        } else {
          // إذا لم يكن المستخدم موجود في Firestore، اعتبره admin (للمستخدم الأول)
          setUserRole('admin');
          setPermissions(getPermissionsForRole('admin'));
          if (!activeTab) {
            setActiveTab('stats');
          }
        }
      }
    } catch (error) {
      console.error('Error loading user permissions:', error);
      // في حالة الخطأ، استخدم صلاحيات viewer
      setUserRole('viewer');
      setPermissions(getPermissionsForRole('viewer'));
      if (!activeTab) {
        setActiveTab('images');
      }
    }
  };

  const getPermissionsForRole = (role) => {
    const rolePermissions = {
      admin: {
        canManageUsers: true,
        canManageImages: true,
        canManageCategories: true,
        canViewStats: true,
        canManageContact: true,
        canManageAbout: true,
        canViewMessages: true,
        canManageTopics: true
      },
      editor: {
        canManageUsers: false,
        canManageImages: true,
        canManageCategories: true,
        canViewStats: false,
        canManageContact: false,
        canManageAbout: false,
        canViewMessages: false,
        canManageTopics: true
      },
      viewer: {
        canManageUsers: false,
        canManageImages: true,
        canManageCategories: false,
        canViewStats: false,
        canManageContact: false,
        canManageAbout: false,
        canViewMessages: false,
        canManageTopics: false
      }
    };
    
    return rolePermissions[role] || rolePermissions.viewer;
  };

  const loadStats = async () => {
    try {
      const count = await getVisitorCount();
      setVisitorCount(count);
      
      const countries = await getCountriesStats();
      setCountriesStats(countries);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadUnreadMessages = async () => {
    try {
      const count = await getUnreadMessagesCount();
      setUnreadMessages(count);
    } catch (error) {
      console.error('Error loading unread messages count:', error);
    }
  };

  const loadTotalMessages = async () => {
    try {
      const messages = await getAllMessages();
      setTotalMessages(messages.length);
    } catch (error) {
      console.error('Error loading total messages count:', error);
    }
  };

  const loadImages = async () => {
    try {
      setLoading(true);
      const allImages = await getAllImages();
      
      // ترتيب الصور حسب تاريخ الإضافة (الأحدث أولاً) - نفس ترتيب الموقع الرئيسي
      const sortedImages = allImages.sort((a, b) => {
        const dateA = new Date(a.createdAt || 0);
        const dateB = new Date(b.createdAt || 0);
        return dateB - dateA; // الأحدث أولاً
      });
      
      setImages(sortedImages);
      
      const visibleCount = sortedImages.filter(img => img.visible !== false).length;
      const hiddenCount = sortedImages.filter(img => img.visible === false).length;
      const withTextCount = sortedImages.filter(img => img.extractedText && img.extractedText.trim().length > 0).length;
      const withoutTextCount = sortedImages.filter(img => !img.extractedText || img.extractedText.trim().length === 0).length;
      
      setStats({
        total: sortedImages.length,
        visible: visibleCount,
        hidden: hiddenCount,
        withText: withTextCount,
        withoutText: withoutTextCount
      });
    } catch (error) {
      console.error('Error loading images:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      onLogout();
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    // التحقق من البيانات
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      setPasswordError('جميع الحقول مطلوبة');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setPasswordError('كلمة المرور الجديدة يجب أن تكون 6 أحرف على الأقل');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('كلمة المرور الجديدة وتأكيدها غير متطابقين');
      return;
    }

    setChangingPassword(true);

    try {
      const { updatePassword, reauthenticateWithCredential, EmailAuthProvider } = await import('firebase/auth');
      const { auth } = await import('../../firebase/config');

      // إعادة المصادقة بكلمة المرور الحالية
      const credential = EmailAuthProvider.credential(
        user.email,
        passwordData.currentPassword
      );
      await reauthenticateWithCredential(auth.currentUser, credential);

      // تحديث كلمة المرور
      await updatePassword(auth.currentUser, passwordData.newPassword);

      setPasswordSuccess('تم تغيير كلمة المرور بنجاح');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });

      setTimeout(() => {
        setShowChangePasswordModal(false);
        setPasswordSuccess('');
      }, 2000);
    } catch (error) {
      console.error('Error changing password:', error);
      if (error.code === 'auth/wrong-password') {
        setPasswordError('كلمة المرور الحالية غير صحيحة');
      } else if (error.code === 'auth/weak-password') {
        setPasswordError('كلمة المرور الجديدة ضعيفة جداً');
      } else {
        setPasswordError('فشل تغيير كلمة المرور. حاول مرة أخرى');
      }
    } finally {
      setChangingPassword(false);
    }
  };

  const handleImageAdded = () => {
    loadImages();
    setActiveTab('images');
  };

  const handleImageUpdated = async () => {
    await loadImages();
    // تحديث الصورة المحددة بعد إعادة تحميل البيانات
    if (selectedImage) {
      const allImages = await getAllImages();
      const sortedImages = allImages.sort((a, b) => {
        const dateA = new Date(a.createdAt || 0);
        const dateB = new Date(b.createdAt || 0);
        return dateB - dateA;
      });
      const updatedImage = sortedImages.find(img => img.id === selectedImage.id);
      if (updatedImage) {
        setSelectedImage(updatedImage);
      }
    }
  };

  return (
    <div className="dashboard">
      <button 
        className="hamburger-btn"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        aria-label="Toggle menu"
      >
        <span></span>
        <span></span>
        <span></span>
      </button>

      <div className={`dashboard-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <h2>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle', marginLeft: '8px' }}>
              <rect x="3" y="3" width="7" height="7"></rect>
              <rect x="14" y="3" width="7" height="7"></rect>
              <rect x="14" y="14" width="7" height="7"></rect>
              <rect x="3" y="14" width="7" height="7"></rect>
            </svg>
            لوحة التحكم
          </h2>
          <p className="user-email">{user?.email}</p>
        </div>

        <nav className="sidebar-nav">
          {permissions.canViewStats && (
            <button 
              className={`nav-item ${activeTab === 'stats' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('stats');
                setSidebarOpen(false);
              }}
            >
              <span className="nav-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="20" x2="18" y2="10"></line>
                  <line x1="12" y1="20" x2="12" y2="4"></line>
                  <line x1="6" y1="20" x2="6" y2="14"></line>
                </svg>
              </span>
              <span className="nav-label">الإحصائيات</span>
            </button>
          )}

          {permissions.canManageImages && (
            <button 
              className={`nav-item ${activeTab === 'images' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('images');
                setSidebarOpen(false);
              }}
            >
              <span className="nav-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                  <circle cx="8.5" cy="8.5" r="1.5"></circle>
                  <polyline points="21 15 16 10 5 21"></polyline>
                </svg>
              </span>
              <span className="nav-label">إدارة التصاميم</span>
              <span className="nav-badge">{stats.total}</span>
            </button>
          )}

          {permissions.canManageImages && (
            <button 
              className={`nav-item ${activeTab === 'add' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('add');
                setSidebarOpen(false);
              }}
            >
              <span className="nav-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
              </span>
              <span className="nav-label">إضافة تصميم جديد</span>
            </button>
          )}

          {permissions.canManageCategories && (
            <button 
              className={`nav-item ${activeTab === 'categories' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('categories');
                setSidebarOpen(false);
              }}
            >
              <span className="nav-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
                  <line x1="7" y1="7" x2="7.01" y2="7"></line>
                </svg>
              </span>
              <span className="nav-label">إدارة التصنيفات</span>
            </button>
          )}

          {permissions.canManageTopics && (
            <button 
              className={`nav-item ${activeTab === 'topics' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('topics');
                setSidebarOpen(false);
              }}
            >
              <span className="nav-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <line x1="16" y1="13" x2="8" y2="13"></line>
                  <line x1="16" y1="17" x2="8" y2="17"></line>
                  <polyline points="10 9 9 9 8 9"></polyline>
                </svg>
              </span>
              <span className="nav-label">إدارة المواضيع</span>
            </button>
          )}

          {permissions.canManageContact && (
            <button 
              className={`nav-item ${activeTab === 'contact' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('contact');
                setSidebarOpen(false);
              }}
            >
              <span className="nav-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                </svg>
              </span>
              <span className="nav-label">إدارة صفحة التواصل</span>
            </button>
          )}

          {permissions.canManageAbout && (
            <button 
              className={`nav-item ${activeTab === 'about' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('about');
                setSidebarOpen(false);
              }}
            >
              <span className="nav-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
              </span>
              <span className="nav-label">إدارة صفحة عن الشيخ</span>
            </button>
          )}

          {permissions.canViewMessages && (
            <button 
              className={`nav-item ${activeTab === 'messages' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('messages');
                setSidebarOpen(false);
                loadUnreadMessages();
              }}
            >
              <span className="nav-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
              </span>
              <span className="nav-label">الرسائل</span>
              {unreadMessages > 0 && (
                <span className="nav-badge">{unreadMessages}</span>
              )}
            </button>
          )}

          {permissions.canManageUsers && (
            <button 
              className={`nav-item ${activeTab === 'users' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('users');
                setSidebarOpen(false);
              }}
            >
              <span className="nav-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
              </span>
              <span className="nav-label">إدارة المستخدمين</span>
            </button>
          )}
        </nav>

        <div className="sidebar-footer">
          <button className="change-password-button" onClick={() => setShowChangePasswordModal(true)}>
            <span>🔑</span>
            تغيير كلمة المرور
          </button>
          <button className="logout-button" onClick={handleLogout}>
            <span>🚪</span>
            تسجيل الخروج
          </button>
        </div>
      </div>

      <div className="dashboard-content">
        <div className="content-header">
          <h1>
            {activeTab === 'images' && (
              <>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle', marginLeft: '8px' }}>
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                  <circle cx="8.5" cy="8.5" r="1.5"></circle>
                  <polyline points="21 15 16 10 5 21"></polyline>
                </svg>
                إدارة التصاميم
              </>
            )}
            {activeTab === 'add' && (
              <>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle', marginLeft: '8px' }}>
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
                إضافة تصميم جديد
              </>
            )}
            {activeTab === 'categories' && (
              <>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle', marginLeft: '8px' }}>
                  <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
                  <line x1="7" y1="7" x2="7.01" y2="7"></line>
                </svg>
                إدارة التصنيفات
              </>
            )}
            {activeTab === 'stats' && (
              <>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle', marginLeft: '8px' }}>
                  <line x1="18" y1="20" x2="18" y2="10"></line>
                  <line x1="12" y1="20" x2="12" y2="4"></line>
                  <line x1="6" y1="20" x2="6" y2="14"></line>
                </svg>
                الإحصائيات
              </>
            )}
            {activeTab === 'contact' && (
              <>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle', marginLeft: '8px' }}>
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                </svg>
                إدارة صفحة التواصل
              </>
            )}
            {activeTab === 'about' && (
              <>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle', marginLeft: '8px' }}>
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
                إدارة صفحة عن الشيخ
              </>
            )}
            {activeTab === 'messages' && (
              <>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle', marginLeft: '8px' }}>
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
                الرسائل
              </>
            )}
            {activeTab === 'users' && (
              <>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle', marginLeft: '8px' }}>
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
                إدارة المستخدمين
              </>
            )}
            {activeTab === 'topics' && (
              <>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle', marginLeft: '8px' }}>
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <line x1="16" y1="13" x2="8" y2="13"></line>
                  <line x1="16" y1="17" x2="8" y2="17"></line>
                  <polyline points="10 9 9 9 8 9"></polyline>
                </svg>
                إدارة المواضيع
              </>
            )}
          </h1>
        </div>

        <div className="content-body">
          {loading && activeTab === 'images' ? (
            <div className="loading-state">
              <div className="spinner-large"></div>
              <p>جاري تحميل التصاميم...</p>
            </div>
          ) : (
            <>
              {activeTab === 'images' && !selectedImage && (
                <ImageManager 
                  images={images} 
                  onImageUpdated={handleImageUpdated}
                  onImageClick={(image) => setSelectedImage(image)}
                />
              )}

              {activeTab === 'images' && selectedImage && (
                <AdminImageDetail
                  image={selectedImage}
                  images={images}
                  currentIndex={images.findIndex(img => img.id === selectedImage.id)}
                  onBack={() => setSelectedImage(null)}
                  onImageUpdated={handleImageUpdated}
                  onNavigate={(image) => setSelectedImage(image)}
                />
              )}

              {activeTab === 'add' && (
                <AddImage onImageAdded={handleImageAdded} />
              )}

              {activeTab === 'categories' && (
                <CategoryManager />
              )}

              {activeTab === 'contact' && (
                <ContactManager />
              )}

              {activeTab === 'about' && (
                <AboutManager />
              )}

              {activeTab === 'messages' && (
                <MessagesManager onUnreadCountChange={setUnreadMessages} />
              )}

              {activeTab === 'users' && (
                <UserManager currentUser={user} />
              )}

              {activeTab === 'topics' && (
                <TopicsManagement />
              )}

              {activeTab === 'stats' && !permissions.canViewStats && (
                <div className="no-permission-message">
                  <div className="no-permission-icon">
                    <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line>
                    </svg>
                  </div>
                  <h2>ليس لديك صلاحية للوصول</h2>
                  <p>هذه الصفحة متاحة للمدير فقط</p>
                </div>
              )}

              {activeTab === 'stats' && permissions.canViewStats && (
                <div className="stats-container">
                  <div className="stat-card">
                    <div className="stat-icon">
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="7" height="7"></rect>
                        <rect x="14" y="3" width="7" height="7"></rect>
                        <rect x="14" y="14" width="7" height="7"></rect>
                        <rect x="3" y="14" width="7" height="7"></rect>
                      </svg>
                    </div>
                    <div className="stat-info">
                      <h3>إجمالي التصاميم</h3>
                      <p className="stat-number">{stats.total}</p>
                    </div>
                  </div>

                  <div className="stat-card">
                    <div className="stat-icon">
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                        <circle cx="12" cy="12" r="3"></circle>
                      </svg>
                    </div>
                    <div className="stat-info">
                      <h3>التصاميم المرئية</h3>
                      <p className="stat-number">{stats.visible}</p>
                    </div>
                  </div>

                  <div className="stat-card">
                    <div className="stat-icon">
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                        <line x1="1" y1="1" x2="23" y2="23"></line>
                      </svg>
                    </div>
                    <div className="stat-info">
                      <h3>التصاميم المخفية</h3>
                      <p className="stat-number">{stats.hidden}</p>
                    </div>
                  </div>

                  <div className="stat-card">
                    <div className="stat-icon">
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                        <circle cx="9" cy="7" r="4"></circle>
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                      </svg>
                    </div>
                    <div className="stat-info">
                      <h3>عدد زوار الموقع</h3>
                      <p className="stat-number">{visitorCount}</p>
                    </div>
                  </div>

                  <div className="stat-card">
                    <div className="stat-icon">
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
                        <line x1="7" y1="7" x2="7.01" y2="7"></line>
                      </svg>
                    </div>
                    <div className="stat-info">
                      <h3>عدد التصنيفات</h3>
                      <p className="stat-number">11</p>
                    </div>
                  </div>

                  <div className="stat-card">
                    <div className="stat-icon">
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                        <line x1="16" y1="13" x2="8" y2="13"></line>
                        <line x1="16" y1="17" x2="8" y2="17"></line>
                        <polyline points="10 9 9 9 8 9"></polyline>
                      </svg>
                    </div>
                    <div className="stat-info">
                      <h3>تصاميم مع تفريغ نصي</h3>
                      <p className="stat-number">{stats.withText}</p>
                    </div>
                  </div>

                  <div className="stat-card">
                    <div className="stat-icon">
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                      </svg>
                    </div>
                    <div className="stat-info">
                      <h3>تصاميم بدون تفريغ نصي</h3>
                      <p className="stat-number">{stats.withoutText}</p>
                    </div>
                  </div>

                  <div className="stat-card">
                    <div className="stat-icon">
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                        <polyline points="22,6 12,13 2,6"></polyline>
                      </svg>
                    </div>
                    <div className="stat-info">
                      <h3>الرسائل المستلمة</h3>
                      <p className="stat-number">{totalMessages}</p>
                    </div>
                  </div>

                  <div className="stat-card">
                    <div className="stat-icon">
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                      </svg>
                    </div>
                    <div className="stat-info">
                      <h3>آخر تحديث</h3>
                      <p className="stat-number">{new Date().toLocaleDateString('ar-SA')}</p>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'stats' && (
                <div className="countries-stats-section">
                  <h2 className="section-title">🌍 إحصائيات الدول</h2>
                  {countriesStats.length > 0 ? (
                    <div className="countries-list">
                      {countriesStats.map((country, index) => (
                        <div key={country.code} className="country-item">
                          <span className="country-rank">#{index + 1}</span>
                          <span className="country-name">{country.name}</span>
                          <span className="country-count">{country.count} زائر</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="no-data-message">
                      <p>📊 لا توجد بيانات بعد. سيتم عرض إحصائيات الدول عند زيارة الموقع من قبل زوار جدد.</p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* نافذة تغيير كلمة المرور */}
      {showChangePasswordModal && (
        <div className="modal-overlay" onClick={() => setShowChangePasswordModal(false)}>
          <div className="modal-content change-password-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>تغيير كلمة المرور</h2>
              <button className="close-btn" onClick={() => setShowChangePasswordModal(false)}>✕</button>
            </div>

            {passwordError && (
              <div className="alert alert-error">
                <span>❌</span>
                <span>{passwordError}</span>
              </div>
            )}

            {passwordSuccess && (
              <div className="alert alert-success">
                <span>✅</span>
                <span>{passwordSuccess}</span>
              </div>
            )}

            <form onSubmit={handleChangePassword} className="change-password-form">
              <div className="form-group">
                <label>كلمة المرور الحالية *</label>
                <input
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  placeholder="أدخل كلمة المرور الحالية"
                  required
                  disabled={changingPassword}
                />
              </div>

              <div className="form-group">
                <label>كلمة المرور الجديدة *</label>
                <input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  placeholder="6 أحرف على الأقل"
                  minLength="6"
                  required
                  disabled={changingPassword}
                />
              </div>

              <div className="form-group">
                <label>تأكيد كلمة المرور الجديدة *</label>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  placeholder="أعد إدخال كلمة المرور الجديدة"
                  minLength="6"
                  required
                  disabled={changingPassword}
                />
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowChangePasswordModal(false)}
                  disabled={changingPassword}
                >
                  إلغاء
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={changingPassword}
                >
                  {changingPassword ? 'جاري التغيير...' : 'تغيير كلمة المرور'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
